import { computed, inject, Injectable } from '@angular/core';
import { BaseWorkflowStateService } from '../../base-workflow-state.service';
import { InternetFlowStateService } from '../internet-flow-state.service';
import { InternetAdditionalOfferData } from '../../../../../../../models/order/workflow/internet/internet-additional-offer.model';
import { AdditionalOfferData, OfferType } from '../../../../../../../models/order/workflow/additional-offer.model';
import { InternetProductResponseService } from '../../../order-api-response/internet-pd-response.service';
import { Choice } from '../../../../../../../models/order/api-models/order-pd-response.model';



@Injectable({ providedIn: 'root' })
export class AdditionalInternetOffersStateService extends BaseWorkflowStateService<InternetAdditionalOfferData> {
    private flowStateService = inject(InternetFlowStateService);
    private internetPdResponseService = inject(InternetProductResponseService);
    override stateKey: string = 'internetOffers';

    customizations() {
        this.internetPdResponseService.setupCustomizations();
        return this.internetPdResponseService.getCustomizations();
    }

    offers = computed(() => {
        return this.state()?.offers ?? [];
    });

    includedOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Required) || [];
    });

    additionalOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Additional) || [];
    });

    equipmentOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Equipment) || [];
    });

    findOfferByCustomizationAndChoice(customizationCode: string, choiceCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode && (offer.choice?.ChoiceCode === choiceCode || offer.choiceCode === choiceCode));
    }

    findOffersByCustomization(customizationCode: string): AdditionalOfferData[] {
        return this.offers().filter(offer => offer.customizationCode === customizationCode);
    }

    findDropdownSelectedOffer(customizationCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode);
    }

    getAllIncludedWithPackageChoices(): AdditionalOfferData[] {
        const choices = this.customizations()?.filter(x => x.customization.Choices.Choice.some((choice: Choice) => choice.IncludedWithPackage && (choice.IncludedWithPackage === true || choice.IncludedWithPackage.toLocaleLowerCase() === 'true')))?.flatMap(cust => cust.customization.Choices.Choice.filter((choice: Choice) => choice.IncludedWithPackage && (choice.IncludedWithPackage === true || choice.IncludedWithPackage.toLocaleLowerCase() === 'true'))) || [];
        const pdDefaultOffers: AdditionalOfferData[] = []
        for (const offer of choices) {
            const customization = this.customizations().find(cust => cust.customization.Choices.Choice.some(choice => choice.ChoiceCode === offer.ChoiceCode));
            const type = customization?.customization.SubGroupName === 'IncludedOffers' ? OfferType.Required : OfferType.Additional;
            pdDefaultOffers.push({ customizationCode: customization?.customization.CustomizationCode, choiceCode: offer.ChoiceCode, type, usoc: offer.Usoc });
        }
        return pdDefaultOffers;
    }

    addOffer(offer: AdditionalOfferData): void {
        const currentState = this.state();
        const existingOfferIndex = currentState?.offers?.findIndex(o => o.choiceCode === offer.choiceCode);
        if (existingOfferIndex > -1) {
            currentState.offers[existingOfferIndex] = offer;
        } else {
            currentState.offers.push(offer);
        }
        this.updateData(currentState);
    }

    removeOffer(choiceCode: string): void {
        const currentState = this.state();
        currentState.offers = currentState.offers?.filter(o => o.choiceCode !== choiceCode);
        this.updateData(currentState);
    }

    addABPoffer(abpEnabled: boolean): void {
        const abpOffer = this.internetPdResponseService.getABPAdditionalOfferData(abpEnabled);
        this.addOffer(abpOffer);
        this.removeOffer(abpEnabled ? 'ATT-INTERNET-ABP-PAPERLESS-YES' : 'ATT-INTERNET-ABP-BOTH-YES');
    }

    protected override saveLocalDraft(): void {
        this.flowStateService.updateData({ [this.stateKey]: this.state() });
    }

    resetData() {
        this.state.set(null);
        this.saveLocalDraft();
    }
}
